import { ICONS } from 'consts/consts';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Card, Table } from 'reactstrap';
import ValidatedInput from 'views/Widgets/Inputs/ValidatedInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';

const ALL_LEVELS = 'all';

type Person = Record<string, unknown>;

interface ApproverRow {
  level: string;
  person: Person | null;
}

interface Props {
  name: string;
  value?: Record<string, unknown>;
  onChange: (value: Record<string, unknown> | null) => void;
  levelOptions?: { id?: string; name?: string }[];
  disabled?: boolean;
}

const rowsFromValue = (value?: Record<string, unknown>): ApproverRow[] => {
  const byLevel = (value?.level_id ?? {}) as Record<string, Person>;
  return Object.entries(byLevel).map(([level, person]) => ({
    level,
    person,
  }));
};

const PromotionPacketApproversInput: FC<Props> = ({
  name,
  value,
  onChange,
  levelOptions,
  disabled = false,
}) => {
  const { formatMessage } = useIntl();
  const [rows, setRows] = useState<ApproverRow[]>(() => rowsFromValue(value));

  const emit = useCallback(
    (newRows: ApproverRow[]) => {
      setRows(newRows);
      const level_id: Record<string, Person> = {};
      for (const row of newRows) {
        if (row.level && row.person) {
          level_id[row.level] = row.person;
        }
      }
      const otherKeys = Object.fromEntries(
        Object.entries(value ?? {}).filter(([key]) => key !== 'level_id')
      );
      if (
        Object.keys(level_id).length === 0 &&
        Object.keys(otherKeys).length === 0
      ) {
        onChange(null);
        return;
      }
      onChange({ ...otherKeys, level_id });
    },
    [onChange, value]
  );

  const levelIds = (levelOptions ?? [])
    .map((option) => option.id)
    .filter((id): id is string => !!id);

  const usedLevels = new Set(rows.map((row) => row.level));

  const levelChoicesForRow = (row: ApproverRow): string[] => {
    const choices = [ALL_LEVELS, ...levelIds];
    if (row.level && !choices.includes(row.level)) {
      choices.push(row.level);
    }
    return choices.filter(
      (level) => level === row.level || !usedLevels.has(level)
    );
  };

  const addRow = useCallback(() => {
    const firstFree = [ALL_LEVELS, ...levelIds].find(
      (level) => !usedLevels.has(level)
    );
    setRows((prev) => [...prev, { level: firstFree ?? '', person: null }]);
  }, [levelIds, usedLevels]);

  return (
    <div>
      {rows.length > 0 && (
        <Card>
          <Table responsive size="sm" className="card-table mb-0">
            <thead>
              <tr>
                <th scope="col" className="pe-1 text-nowrap w-25">
                  <div className="text-muted">
                    {formatMessage({
                      id: 'app.views.administration.content_administration.templates.promo.approvers.level_column',
                      defaultMessage: 'Level',
                    })}
                  </div>
                </th>
                <th scope="col" className="pe-1 text-nowrap">
                  <div className="text-muted">
                    {formatMessage({
                      id: 'app.views.administration.content_administration.templates.promo.approvers.approver_column',
                      defaultMessage: 'Approver',
                    })}
                  </div>
                </th>
                <th scope="col" className="pe-1 text-nowrap">
                  <div className="text-muted">
                    {formatMessage({
                      id: 'app.views.administration.content_administration.templates.promo.approvers.action_column',
                      defaultMessage: 'Action',
                    })}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td className="pe-1 align-middle">
                    {/* @ts-expect-error */}
                    <ValidatedInput
                      name={`${name}_level_${index}`}
                      type={INPUT_TYPES.DROPDOWN}
                      objects={levelChoicesForRow(row).map((level) => ({
                        id: level,
                        name:
                          level === ALL_LEVELS
                            ? formatMessage({
                                id: 'app.views.administration.content_administration.templates.promo.approvers.all_levels',
                                defaultMessage: 'All levels',
                              })
                            : level,
                      }))}
                      value={row.level}
                      disabled={disabled}
                      formGroupClassName="mb-0"
                      noFormGroupMargin={true}
                      onChange={(e) => {
                        const newRows = rows.map((r, i) =>
                          i === index ? { ...r, level: e.target.value } : r
                        );
                        emit(newRows);
                      }}
                    />
                  </td>
                  <td className="pe-1 align-middle">
                    {/* @ts-expect-error */}
                    <ValidatedInput
                      name={`${name}_person_${index}`}
                      type={INPUT_TYPES.PEOPLE_EDITOR}
                      value={row.person ? [row.person] : []}
                      maxSelections={1}
                      allowSelf={true}
                      disabled={disabled}
                      formGroupClassName="mb-0"
                      noFormGroupMargin={true}
                      onChange={(e) => {
                        const people = (e.target.value ?? []) as Person[];
                        const newRows = rows.map((r, i) =>
                          i === index
                            ? {
                                ...r,
                                person: people[people.length - 1] ?? null,
                              }
                            : r
                        );
                        emit(newRows);
                      }}
                      placeholder={formatMessage({
                        id: 'app.views.administration.content_administration.templates.promo.approvers.person_placeholder',
                        defaultMessage: 'Search for a person...',
                      })}
                    />
                  </td>
                  <td className="pe-1 align-middle">
                    <Button
                      color="white"
                      size="sm"
                      disabled={disabled}
                      onClick={() => emit(rows.filter((_r, i) => i !== index))}
                      aria-label={formatMessage({
                        id: 'app.views.administration.content_administration.templates.promo.approvers.remove',
                        defaultMessage: 'Remove approver',
                      })}
                    >
                      <i className={ICONS.TRASH} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      <Button color="white" disabled={disabled} onClick={addRow}>
        <i className={ICONS.ADD} />{' '}
        <FormattedMessage
          id="app.views.administration.content_administration.templates.promo.approvers.add"
          defaultMessage="Add approver"
        />
      </Button>
    </div>
  );
};

export default React.memo(PromotionPacketApproversInput);
